/**
 * HowThisWorks — reusable "Explain this page" modal.
 *
 * Drop-in replacement for the inline "Understanding X" modals that
 * already exist on a handful of pages (Contracts dashboard, ABAC
 * Policies, Risks register). Mounted as a HelpCircle icon button in
 * the page header; click opens a centered modal explaining what
 * the page is for.
 *
 *   <HowThisWorks
 *     title="Understanding the Risk Register"
 *     overview="The risk register tracks every risk, issue, and opportunity..."
 *     sections={[
 *       { icon: <Shield />, label: 'Risk Articulation', description: 'If/Then format...' },
 *       ...
 *     ]}
 *     whatYouCanDo={[
 *       'Click a row to expand the disposition + steps',
 *       'Press +New Risk to capture a new entry',
 *     ]}
 *     tips={['The trend column shows last-30-day direction-of-travel.']}
 *   />
 */

import { useEffect, useState, type ReactNode } from 'react'
import {
  HelpCircle,
  X,
  BookOpen,
  LayoutGrid,
  MousePointerClick,
  Lightbulb,
  Sparkles,
  Compass,
  ArrowRight,
  ChevronDown,
  MessageCircleQuestion,
} from 'lucide-react'
import { getHelp } from './helpContent'
import { usePageTourStore } from './pageTour/pageTourStore'
import { hasPageTour, getPageTour } from './pageTour/pageTours'
import { useAppStore } from '@/store/appStore'
import { HelpText } from './HelpText'

// ─── First-visit nudge ───────────────────────────────────────────────────────
// Pages whose help was never opened get a one-time gentle pulse-dot on the ?
// button — an invitation, never an interruption (design pick 2026-07-19:
// "subtle nudge" over auto-open). Seen-state is per persona in localStorage
// (the admin-hub personalization pattern), cleared naturally by a new persona.
function helpSeenKey(userId: string): string {
  return `help-seen:${userId}`
}
function hasSeenHelp(userId: string, helpKey: string): boolean {
  try {
    const raw = localStorage.getItem(helpSeenKey(userId))
    return raw ? (JSON.parse(raw) as string[]).includes(helpKey) : false
  } catch { return true } // storage unavailable → never nudge
}
function markHelpSeen(userId: string, helpKey: string): void {
  try {
    const raw = localStorage.getItem(helpSeenKey(userId))
    const list = raw ? (JSON.parse(raw) as string[]) : []
    if (!list.includes(helpKey)) {
      localStorage.setItem(helpSeenKey(userId), JSON.stringify([...list, helpKey]))
    }
  } catch { /* ignore */ }
}

export interface HowThisWorksSection {
  icon: ReactNode
  label: string
  description: string
}

export interface HowThisWorksProps {
  /** Modal title — usually "Understanding {Page Name}". */
  title: string
  /** One or more short paragraphs that explain the page in plain English. */
  overview: string | string[]
  /** Optional grid of icon + label + 1-line description tiles for major sections. */
  sections?: HowThisWorksSection[]
  /** Optional bullet list of actions a user can perform. */
  whatYouCanDo?: string[]
  /** Optional 'pro tips' / non-obvious notes. */
  tips?: string[]
  /**
   * Optional "Common questions" — the real questions users ask about this
   * page, each answered in 1-3 sentences (Help v2 answer-first standard,
   * 2026-07-23). Rendered as an accordion in both the modal and the Explore
   * rail. Write from actual confusions surfaced in walks, not marketing FAQs.
   */
  questions?: Array<{ q: string; a: string }>

  /** Trigger button placement override. Defaults to inline. */
  className?: string
}

/** Two ways to invoke:
 *    <HowThisWorks helpKey="portfolio-dashboard" />            // from registry
 *    <HowThisWorks title="..." overview="..." />               // inline (legacy) */
type Props = HowThisWorksProps | { helpKey: string; className?: string }

export function HowThisWorks(props: Props) {
  // Resolve helpKey form -> HowThisWorksProps; pass through inline form.
  if ('helpKey' in props) {
    const entry = getHelp(props.helpKey)
    if (entry) return <HowThisWorksInner {...entry} className={props.className} tourKey={props.helpKey} />
    // Tour-only key: still render the launcher — it drops straight into
    // Explore (the modal branch is unreachable when a tour exists).
    if (hasPageTour(props.helpKey)) {
      return <HowThisWorksInner title="" overview="" className={props.className} tourKey={props.helpKey} />
    }
    return null
  }
  return <HowThisWorksInner {...props} />
}

/**
 * Tile palette — rotated across the section grid so each card carries a
 * distinct visual tone, instead of every tile being plain slate.
 * Indexed % palette.length so an arbitrary number of sections still works.
 * Exported: the Explore rail (PageExploreOverlay) rotates the same palette
 * over its section tiles so modal and rail share one visual language.
 */
export const TILE_PALETTE: Array<{
  bg: string
  border: string
  iconBg: string
  iconText: string
  label: string
}> = [
  {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-700',
    label: 'text-blue-900',
  },
  {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-700',
    label: 'text-emerald-900',
  },
  {
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    iconBg: 'bg-violet-100',
    iconText: 'text-violet-700',
    label: 'text-violet-900',
  },
  {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-800',
    label: 'text-amber-900',
  },
  {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-700',
    label: 'text-rose-900',
  },
  {
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    iconBg: 'bg-cyan-100',
    iconText: 'text-cyan-700',
    label: 'text-cyan-900',
  },
]

function SectionHeading({
  icon,
  title,
  count,
}: {
  icon: ReactNode
  title: string
  count?: number
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-mach12-primary/10 text-mach12-primary [&>svg]:h-4 [&>svg]:w-4">
        {icon}
      </span>
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      {typeof count === 'number' && (
        <span className="text-[11px] font-mono px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 tabular-nums">
          {count}
        </span>
      )}
    </div>
  )
}

function HowThisWorksInner({
  title, overview, sections, whatYouCanDo, tips, questions, className, tourKey,
}: HowThisWorksProps & { tourKey?: string }) {
  const [open, setOpen] = useState(false)
  const [openQ, setOpenQ] = useState<number | null>(null)
  const overviewParas = Array.isArray(overview) ? overview : [overview]
  const startExplore = usePageTourStore((s) => s.startExplore)
  const tourAvailable = tourKey ? hasPageTour(tourKey) : false
  const userId = useAppStore((s) => s.currentUser?.id ?? '_anon')
  // First-visit nudge: pulse-dot until this persona opens this page's help once.
  const [nudge, setNudge] = useState(() => (tourKey ? !hasSeenHelp(userId, tourKey) : false))
  // Personas switch without remounting the page header — recompute per identity.
  useEffect(() => {
    setNudge(tourKey ? !hasSeenHelp(userId, tourKey) : false)
  }, [userId, tourKey])
  // Single source of truth for the per-section breakdown: the guided-tour steps.
  // When a page has a tour, the modal renders those same steps as its
  // "Sections on this page" (so the read view and the guided walk are one
  // entity), and the button below walks them spotlighted on the page.
  const tourSteps = tourKey && tourAvailable ? getPageTour(tourKey) : null

  return (
    <>
      <button
        onClick={() => {
          if (tourKey) {
            markHelpSeen(userId, tourKey)
            setNudge(false)
          }
          // Explore is the core help surface: when a tour exists, the help
          // button drops straight into it (the rail carries the "What is this
          // page?" summary on top). Pages without a tour open the read modal.
          if (tourAvailable && tourKey) startExplore(tourKey)
          else setOpen(true)
        }}
        className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-blue-600 hover:border-blue-300 transition-colors text-xs ${className ?? ''}`}
        title={tourAvailable ? 'Explore this page' : 'Explain this page'}
        aria-label={tourAvailable ? 'Explore this page' : 'Explain this page'}
      >
        <HelpCircle size={14} />
        {nudge && (
          <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-500" />
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-3xl w-full mx-4 max-h-[88vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Hero header — gradient band with the title + close affordance */}
            <div className="relative bg-gradient-to-br from-mach12-primary via-mach12-primary to-mach12-darker text-white">
              <div className="absolute inset-0 opacity-20 pointer-events-none bg-[radial-gradient(circle_at_top_right,white,transparent_60%)]" />
              <div className="relative px-6 py-5 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm ring-1 ring-white/25 shrink-0">
                    <Sparkles size={20} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-white/70">
                      How this page works
                    </div>
                    <h3 className="text-xl font-semibold leading-tight truncate">{title}</h3>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white shrink-0"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="px-6 py-5 space-y-6 overflow-y-auto">
              {/* Guided-tour launcher — one button. The same sections it walks
                  are also listed below to read, so this modal is the single
                  help entity (read it, or take the tour). Shown if a tour exists. */}
              {tourAvailable && tourKey && (
                <button
                  onClick={() => { setOpen(false); startExplore(tourKey) }}
                  className="group flex w-full items-center gap-3 rounded-xl border border-brand-200 bg-gradient-to-br from-brand-50 to-indigo-50 p-3.5 text-left transition-all hover:border-brand-400 hover:shadow-sm"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600 text-white shrink-0">
                    <Compass size={20} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-brand-900 leading-tight flex items-center gap-1.5">
                      Take a guided tour <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                    </div>
                    <p className="text-[12px] text-slate-600 leading-snug mt-0.5">
                      Walk each section spotlighted on the live page — or read the same sections below.
                    </p>
                  </div>
                </button>
              )}

              {/* Overview — single highlighted callout instead of bare paragraphs */}
              <div>
                <SectionHeading icon={<BookOpen />} title="What is this page?" />
                <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 space-y-2">
                  {overviewParas.map((p, i) => (
                    <p key={i} className="text-sm text-slate-700 leading-relaxed">
                      <HelpText text={p} onNavigate={() => setOpen(false)} />
                    </p>
                  ))}
                </div>
              </div>

              {/* Sections — when the page has a guided tour and no hand-authored
                  tiles, render the tour's steps (numbered, matching the rail) so
                  the read list and the guided walk are one and the same. */}
              {(!sections || sections.length === 0) && tourSteps && tourSteps.length > 0 && (
                <div>
                  <SectionHeading
                    icon={<LayoutGrid />}
                    title="Sections on this page"
                    count={tourSteps.length}
                  />
                  <div className="space-y-2">
                    {tourSteps.map((s, i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-slate-200 bg-white p-3 transition-all hover:shadow-sm hover:-translate-y-0.5"
                      >
                        <div className="flex items-start gap-2.5">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-brand-700 text-[11px] font-bold tabular-nums shrink-0">
                            {i + 1}
                          </span>
                          <div className="min-w-0">
                            <div className="text-[13px] font-semibold text-slate-800 leading-tight">{s.title}</div>
                            {s.body && <p className="text-[12px] text-slate-600 leading-snug mt-0.5">{s.body}</p>}
                            {s.points && s.points.length > 0 && (
                              <ul className="mt-1 space-y-0.5">
                                {s.points.map((p, k) => (
                                  <li key={k} className="flex items-start gap-1.5 text-[11px] text-slate-500 leading-snug">
                                    <span className="inline-block h-1 w-1 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                                    <span>{p}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sections grid — color-rotated tiles, larger icon medallion */}
              {sections && sections.length > 0 && (
                <div>
                  <SectionHeading
                    icon={<LayoutGrid />}
                    title="Sections on this page"
                    count={sections.length}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {sections.map((s, i) => {
                      const tone = TILE_PALETTE[i % TILE_PALETTE.length]!
                      return (
                        <div
                          key={i}
                          className={`group rounded-xl border ${tone.border} ${tone.bg} p-3 transition-all hover:shadow-sm hover:-translate-y-0.5`}
                        >
                          <div className="flex items-start gap-2.5">
                            <span
                              className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${tone.iconBg} ${tone.iconText} shrink-0 ring-1 ring-inset ring-white/40 [&>svg]:h-4 [&>svg]:w-4`}
                            >
                              {s.icon}
                            </span>
                            <div className="min-w-0">
                              <div className={`text-[13px] font-semibold ${tone.label} leading-tight mb-0.5`}>
                                {s.label}
                              </div>
                              <p className="text-[12px] text-slate-600 leading-snug">
                                {s.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Common questions — answer-first accordion (Help v2) */}
              {questions && questions.length > 0 && (
                <div>
                  <SectionHeading
                    icon={<MessageCircleQuestion />}
                    title="Common questions"
                    count={questions.length}
                  />
                  <div className="space-y-1.5">
                    {questions.map((item, i) => {
                      const isOpen = openQ === i
                      return (
                        <div
                          key={i}
                          className={`rounded-xl border transition-colors ${isOpen ? 'border-brand-300 bg-brand-50/40' : 'border-slate-200 bg-white hover:border-brand-200'}`}
                        >
                          <button
                            onClick={() => setOpenQ(isOpen ? null : i)}
                            aria-expanded={isOpen}
                            className="flex w-full items-start gap-2.5 p-2.5 text-left"
                          >
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-brand-700 text-[11px] font-bold shrink-0">?</span>
                            <span className="text-sm font-medium text-slate-800 leading-snug pt-0.5 flex-1">{item.q}</span>
                            <ChevronDown size={14} className={`mt-1.5 shrink-0 text-slate-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                          </button>
                          {isOpen && (
                            <p className="px-2.5 pb-2.5 pl-11 text-[13px] text-slate-600 leading-relaxed">
                              <HelpText text={item.a} onNavigate={() => setOpen(false)} />
                            </p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* What you can do — numbered step flow, not bullets */}
              {whatYouCanDo && whatYouCanDo.length > 0 && (
                <div>
                  <SectionHeading
                    icon={<MousePointerClick />}
                    title="What you can do from here"
                    count={whatYouCanDo.length}
                  />
                  <ol className="space-y-1.5">
                    {whatYouCanDo.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-2.5 hover:border-blue-200 hover:bg-blue-50/30 transition-colors"
                      >
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-blue-100 text-blue-700 text-[11px] font-bold font-mono tabular-nums shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-sm text-slate-700 leading-snug pt-0.5"><HelpText text={item} onNavigate={() => setOpen(false)} /></span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Pro tips — bolder amber card, lightbulb badge */}
              {tips && tips.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 overflow-hidden">
                  <div className="px-4 py-2.5 bg-amber-100/60 border-b border-amber-200 flex items-center gap-2">
                    <Lightbulb size={15} className="text-amber-700" />
                    <span className="text-xs font-bold uppercase tracking-wide text-amber-900">
                      Pro tips
                    </span>
                  </div>
                  <ul className="px-4 py-3 space-y-1.5">
                    {tips.map((t, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-[13px] text-amber-900 leading-snug"
                      >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                        <span><HelpText text={t} onNavigate={() => setOpen(false)} /></span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Footer — the tour launchers now lead the body (top chooser), so
                the footer is just the keyboard hint + dismiss. */}
            <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3 text-[11px] text-slate-500">
              <span>
                {tourAvailable
                  ? 'Pick a guided walk above, or read the summary.'
                  : <>Press <kbd className="px-1.5 py-0.5 rounded border border-slate-300 bg-white font-mono text-[10px]">Esc</kbd> or click outside to close</>}
              </span>
              <div className="flex items-center gap-3 shrink-0">
                <a
                  href="/help"
                  onClick={() => setOpen(false)}
                  className="text-slate-500 hover:text-mach12-primary hover:underline font-medium"
                >
                  Browse all help
                </a>
                <button
                  onClick={() => setOpen(false)}
                  className="text-mach12-primary hover:underline font-medium"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
